import { apiSlice } from '../../../apis/api';
import { CalculationsCollection } from '../../../swagger/collections';
import {
  CalculationEvaluationRatesSummaryResponse,
  CalculationStatisticClusterType,
  Operation,
  PapPackingPictureType,
  SourceStoreEvaluationRatesResponse,
  TargetStoreEvaluationRatesResponse,
} from '@ydistri/api-sdk';
import { getTags } from '../../../apis/apiLib';

const { TAGS, TAGS_ARRAY } = getTags('calculationEvaluations');

export interface GetEvaluationsRequest {
  calculationId: number;
  departmentId?: number;
  clusterType?: CalculationStatisticClusterType;
}

export interface GetEvaluationsSummaryRequest {
  calculationId: number;
  clusterType?: CalculationStatisticClusterType;
}

export interface GetSourceStoreTargetStoresExecutionRatesRequest extends GetEvaluationsRequest {
  sourceStoreId: number;
}

export interface GetCalculationStatisticsSourceStoresEvaluationRatesCsvFileRequest {
  calculationId: number;
  clusterType?: CalculationStatisticClusterType;
}

export interface GetPackingPictureFileRequest {
  calculationId: number;
  sourceStoreId: number;
  targetStoreId: number;
  departmentId?: number;
  boxNumber: number;
  packingPictureType: PapPackingPictureType;
}

export interface GetPackingPictureFileData {
  url: string;
}

const evaluationsRequestKey = (a: GetEvaluationsRequest) =>
  `${a.calculationId}-${a.departmentId ?? 'x'}`;

export const apiCalculationEvaluations = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getEvaluationSummary: builder.query<
        CalculationEvaluationRatesSummaryResponse,
        GetEvaluationsSummaryRequest
      >({
        queryFn: async ({ calculationId, clusterType }) => {
          const { data } = await CalculationsCollection.getCalculationStatisticsEvaluationRates(
            calculationId,
            { clusterType: clusterType },
          );

          return {
            data: data.data,
          };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationEvaluationSummary,
            id: `evalSummary-V2-${arg.calculationId}`,
          },
        ],
      }),
      getStoreEvaluationRates: builder.query<
        SourceStoreEvaluationRatesResponse[],
        GetEvaluationsRequest
      >({
        queryFn: async ({ calculationId, departmentId, clusterType }) => {
          const conditions = [];
          if (departmentId) {
            conditions.push({
              fieldName: 'departmentId',
              operation: Operation.Eq,
              value: departmentId,
            });
          }

          const { data } =
            await CalculationsCollection.getCalculationStatisticsSourceStoresEvaluationRates(
              calculationId,
              {
                clusterType,
                conditions,
              },
            );

          return { data: data.data };
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.calculationStoreExecutionRates, id: evaluationsRequestKey(arg) },
        ],
      }),
      getSourceStoreTargetStoresCategoriesEvaluationRates: builder.query<
        TargetStoreEvaluationRatesResponse[],
        GetSourceStoreTargetStoresExecutionRatesRequest
      >({
        queryFn: async ({ calculationId, sourceStoreId, departmentId }) => {
          // If departmentId is provided, we need to call a different endpoint
          const { data } = departmentId
            ? await CalculationsCollection.getCalculationStatisticsSourceStoreTargetStoresExecutionRatesByDepartment(
                calculationId,
                departmentId,
                sourceStoreId,
              )
            : await CalculationsCollection.getCalculationStatisticsSourceStoreTargetStoresExecutionRates(
                calculationId,
                sourceStoreId,
              );

          return {
            data: data.data,
          };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationSourceStoreTargetStoresExecutionRates,
            id: evaluationsRequestKey(arg),
          },
        ],
      }),
      getCalculationStatisticsSourceStoresEvaluationRatesCsvFile: builder.query<
        // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- result may be void here
        Blob | void,
        GetCalculationStatisticsSourceStoresEvaluationRatesCsvFileRequest
      >({
        queryFn: async arg => {
          const response =
            await CalculationsCollection.getCalculationStatisticsSourceStoresEvaluationRatesCsvFile(
              arg.calculationId,
              arg.clusterType ? { clusterType: arg.clusterType } : undefined,
              { format: 'blob' },
            );
          const blob = response.data;
          return { data: blob };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationSourceStoreCsvFile,
            id: `calculationSourceStoreCsvFile-${arg.calculationId}`,
          },
        ],
      }), // end of getCalculationStatisticsSourceStoresEvaluationRatesCsvFile()
      getPackingPictureFile: builder.query<GetPackingPictureFileData, GetPackingPictureFileRequest>(
        {
          queryFn: async arg => {
            const response = await CalculationsCollection.getPackingPictureFile(
              arg.calculationId,
              arg.sourceStoreId,
              arg.targetStoreId,
              arg.boxNumber,
              arg.packingPictureType,
              { format: 'blob' },
            );
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- needed for the createObjectURL
            const blob: Blob = response.data as unknown as Blob;
            return { data: { url: URL.createObjectURL(blob) } };
          },
          onCacheEntryAdded: async (_, { cacheDataLoaded, cacheEntryRemoved }) => {
            const data = await cacheDataLoaded;
            await cacheEntryRemoved;
            if (data.data.url) {
              URL.revokeObjectURL(data.data.url);
            }
          },
          providesTags: (result, error, arg) => {
            return [
              {
                type: TAGS.calculationPackingPictureFile,
                id: `packingPicture-${arg.calculationId}-${arg.sourceStoreId}-${arg.targetStoreId}-${arg.departmentId ?? 'x'}-${arg.boxNumber}-${arg.packingPictureType}`,
              },
            ];
          },
        },
      ),
    }),
  });

export const {
  useGetEvaluationSummaryQuery,
  useGetStoreEvaluationRatesQuery,
  useGetSourceStoreTargetStoresCategoriesEvaluationRatesQuery,
  useLazyGetCalculationStatisticsSourceStoresEvaluationRatesCsvFileQuery,
  useGetPackingPictureFileQuery,
} = apiCalculationEvaluations;
